/**
 * permissions.ts — Zod schema for the WS-ENT-3 permissions endpoint.
 *
 * Wire contract (BE 46116f1c):
 *   GET /api/me/permissions
 *   → {
 *       permissions: string[],   // e.g. ["mentorship.report.finalize", …]
 *       role: string,             // canonical viewer role label
 *       cache_max_age: number,    // BE-suggested stale window (seconds)
 *     }
 *
 * Boundary discipline (ADR-0002 §2.2):
 *   - `.strict()` — UNKNOWN keys trip the parse so a backend serializer
 *     drift surfaces here rather than as `undefined` three components
 *     downstream.
 *   - `permissions` is a STRING ARRAY at the wire layer; the FE context
 *     converts to a `Set<string>` for O(1) membership lookups. The
 *     wire shape stays an array because JSON has no Set; we keep the
 *     conversion in the context layer (consumer-owned), not the schema.
 *   - `cache_max_age` is non-negative integer seconds — the BE hints at
 *     5 minutes (300) today; tolerating 0 lets the BE force a no-cache
 *     posture without a schema change.
 *
 * Why `string` (not enum) for `permissions[]`: the permission catalogue
 * is owned by the backend; treating each entry as a free string lets the
 * BE ship new permissions without an FE schema bump. Consumers branch on
 * the literal they expect via `Set.has(key)`.
 *
 * Why `string` (not enum) for `role`: same forward-compat reasoning —
 * we never need to discriminate on `role` at the wire boundary; the FE
 * uses it for display + analytics only.
 */

import { z } from 'zod';

export const PermissionsSchema = z.object({
    permissions: z.array(z.string().min(1)),
    role: z.string().min(1),
    cache_max_age: z.number().int().nonnegative(),
}).strict();

export type PermissionsPayload = z.infer<typeof PermissionsSchema>;
